import { useState, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);

  useEffect(() => {
    let didCheckInitialSession = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Only check admin on actual auth events (not initial)
        if (session?.user && event !== 'INITIAL_SESSION') {
          setLoading(true);
          checkAdminStatus(session.user.id).finally(() => setLoading(false));
        } else if (!session) {
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session (only once)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (didCheckInitialSession) return;
      didCheckInitialSession = true;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        setLoading(true);
        checkAdminStatus(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    // Prevent multiple simultaneous calls
    if (isCheckingAdmin) return;
    
    setIsCheckingAdmin(true);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      if (!error && data) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error checking admin status:', error);
      }
      setIsAdmin(false);
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
  };

  return { user, session, loading, isAdmin, signOut };
};
